import { Injectable } from '@angular/core';
// import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UtilsService } from '../utils/utils.service';
import { ADMIN_SETTINGS_CONFIG, getAllPermissionKeys } from '../../constants/admin-settings.config';


@Injectable({
  providedIn: 'root'
})
export class AdminService {

  constructor(
    // private http: HttpClient,
    private utilsService: UtilsService
  ) {}

  /**
   * Create a new admin rule
   */
  createRule(ruleData: {
    account_id: string;
    brand_id ? : string;
    rules_applied_on: 'account' | 'project' | 'default_admin';
    title: string;
    permission_type: 'READ' | 'WRITE';
    permission_list: string[];
    user_type_for_permission: 'HIERARCHY_LEVEL' | 'HIERARCHY_VALUE' | 'CUSTOM';
    custom_list ? : string[];
  }) {
    const url = this.utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/create_dashboard_tab_visibilities_rules');
    return this.utilsService.postAPICall(url, ruleData, this.utilsService.setHeaders())
  }


  /**
   * Resolve manager email — mirrors API resolveManagerEmail (email_address, then user_name).
   */
  resolveManagerEmail(manager: { email_address?: string; user_name?: string; email?: string } | null | undefined): string {
    const emailAddress = String(manager?.email_address ?? '').trim().toLowerCase();
    if (emailAddress) {
      return emailAddress;
    }

    const userName = String(manager?.user_name ?? '').trim().toLowerCase();
    if (userName) {
      return userName;
    }

    return String(manager?.email ?? '').trim().toLowerCase();
  }

  /**
   * Get admin rules by brand_id with pagination
   */
  getRules(brand_id: string, page_number: number = 1, limit: number = 1000) {
    const url = this.utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/dashboard_tab_visibilities_rules');
    return this.utilsService.getAPICall(url, this.utilsService.setHeaderWithParams({ 
      unique_id: brand_id,
      limit: limit,
      page_number: page_number
    }))
  }

  /**
   * Update an existing admin rule
   */
  updateRule(rule_id: string, ruleData: {
    rules_applied_on: 'account' | 'project' | 'default_admin';
    title: string;
    permission_type: 'READ' | 'WRITE';
    permission_list: string[];
    user_type_for_permission: 'HIERARCHY_LEVEL' | 'HIERARCHY_VALUE' | 'CUSTOM';
    custom_list?: string[];
  }) {
    const url = this.utilsService.apiUrlGeneration(environment.webPrivateV1, '/configs/update_dashboard_tab_visibilities_rules');
    return this.utilsService.putAPICall(url, { rule_id, ...ruleData }, this.utilsService.setHeaders())
  }

  /**
   * Validate if a user has the required permissions using existing rules
   * @param rules - Array of existing rules from getRules()
   * @param permission_list - Array of permission strings to check against
   * @param manager_email - Email address of the manager/user to validate
   * @param required_permission_type - 'READ' or 'WRITE' permission type to validate
   * @returns boolean - true if user has permission, false otherwise
   */
  validatePermission(rules: any[], permission_list: string[], manager_email: string, required_permission_type: 'READ' | 'WRITE'): boolean {
    // Find rules that match the permission list and permission type
    const matchingRules = rules.filter(rule => 
      rule.permission_type === required_permission_type &&
      this.hasMatchingPermissions(rule.permission_list, permission_list)
    );

    if (matchingRules.length === 0) {
      return false; // No rules found for this permission type and list
    }

    // Check if user has access based on user_type_for_permission
    for (const rule of matchingRules) {
      if (this.userHasAccess(rule, manager_email)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if rule permissions match the required permission list
   * @param rulePermissions - Permissions from the rule
   * @param requiredPermissions - Required permissions to check
   * @returns boolean
   */
  private hasMatchingPermissions(rulePermissions: string[], requiredPermissions: string[]): boolean {
    return requiredPermissions.some(required => 
      rulePermissions.includes(required)
    );
  }

  /**
   * Check if user has access based on rule's user_type_for_permission
   * @param rule - The rule to check against
   * @param manager_email - User's email address
   * @returns boolean
   */
  private userHasAccess(rule: any, manager_email: string): boolean {
    switch (rule.user_type_for_permission) {
      case 'CUSTOM': {
        const normalizedEmail = String(manager_email ?? '').trim().toLowerCase();
        return Array.isArray(rule.custom_list)
          && rule.custom_list.some((email: string) => String(email ?? '').trim().toLowerCase() === normalizedEmail);
      }
      
      case 'HIERARCHY_LEVEL':
        // For hierarchy level, you might need to implement hierarchy checking logic
        // This would depend on your hierarchy structure
        // For now, returning true as placeholder
        return true;
      
      case 'HIERARCHY_VALUE':
        // For hierarchy value, you might need to implement hierarchy checking logic
        // This would depend on your hierarchy structure
        // For now, returning true as placeholder
        return true;
      
      default:
        return false;
    }
  }

  /**
   * Create a dictionary of permissions with read/write access for a given manager (default type)
   * @param rules - Array of all rules from getRules()
   * @param manager_email - Email address of the manager/user
   * @param default_admin - Default admin options/details (similar to account/project details)
   * @returns Object with permission names as keys and access levels as values
   */
  createPermissionDictionaryDefault(rules: any[], manager_email: string, default_admin: any = {}): { [permission: string]: { read: boolean; write: boolean } } {
    return this.createPermissionDictionary(rules, manager_email, default_admin, 'default');
  }

  /**
   * Create a dictionary of permissions with read/write access for a given manager
   * @param rules - Array of all rules from getRules()
   * @param manager_email - Email address of the manager/user
   * @param details - Brand details (for projects) or Account details (for accounts)
   * @param context - Context type: 'project', 'account', or 'default'
   * @returns Object with permission names as keys and access levels as values
   */
  createPermissionDictionary(rules: any[], manager_email: string, details: any, context: 'project' | 'account' | 'default' = 'project'): { [permission: string]: { read: boolean; write: boolean } } {
    console.log("manager_email", manager_email, "context", context);
    const permissionDict: { [permission: string]: { read: boolean; write: boolean } } = {};

    // Step 1: Check if user is the creator
    if (context === 'project' && this.isProjectCreator(manager_email, details)) {
      return this.grantAllPermissions(context);
    } else if ((context === 'account' || context === 'default') && this.isAccountCreator(manager_email, details)) {
      return this.grantAllPermissions(context);
    }

    // Step 2: Check if manager_email is in client operations info
    if (context === 'project' && this.checkClientOpsAccess(manager_email, details)) {
      return this.grantAllPermissions(context);
    } else if ((context === 'account' || context === 'default') && this.checkClientPocAccess(manager_email, details)) {
      return this.grantAllPermissions(context);
    }

    // Step 3: Litmusworld users — admin console uses rule-based access when rules exist
    if (manager_email.includes('litmusworld')) {
      if (context === 'default' && rules.length > 0) {
        return this.processRulesOnly(rules, manager_email);
      }
      return this.processLitmusworldUser(rules, manager_email, context);
    }

    // Step 4: If manager_email doesn't fall in above points, check only rules
    return this.processRulesOnly(rules, manager_email);
  }

  /**
   * Check if user is the project creator
   * @param manager_email - User's email address
   * @param brand_details - Brand details object
   * @returns boolean - true if user is project creator
   */
  private isProjectCreator(manager_email: string, brand_details?: any): boolean {
    if (!brand_details || !brand_details.created_by || !brand_details.created_by.user_name) {
      return false;
    }
    return brand_details.created_by.user_name === manager_email;
  }

  /**
   * Check if user is the account creator
   * @param manager_email - User's email address
   * @param account_details - Account details object
   * @returns boolean - true if user is account creator
   */
  private isAccountCreator(manager_email: string, account_details?: any): boolean {
    if (!account_details || !account_details.created_by || !account_details.created_by.user_name) {
      return false;
    }
    return account_details.created_by.user_name === manager_email;
  }

  /**
   * Grant all read and write permissions from ADMIN_SETTINGS_CONFIG
   * @param context - 'project', 'account', or 'default' context
   * @returns Object with all permissions set to read: true, write: true
   */
  private grantAllPermissions(context: 'project' | 'account' | 'default' = 'project'): { [permission: string]: { read: boolean; write: boolean } } {
    // For 'default' context, use 'default_admin' permissions
    const contextForPermissions = context === 'default' ? 'default_admin' : context;
    const allPermissionKeys = getAllPermissionKeys(contextForPermissions);
    const permissionDict: { [permission: string]: { read: boolean; write: boolean } } = {};
    
    allPermissionKeys.forEach(permission => {
      permissionDict[permission] = { read: true, write: true };
    });
    
    return permissionDict;
  }

  /**
   * Process litmusworld users - grant all read permissions + rule-based write permissions
   * @param rules - Array of rules
   * @param manager_email - User's email address
   * @param context - 'project', 'account', or 'default' context
   * @returns Object with all read permissions + rule-based write permissions
   */
  private processLitmusworldUser(rules: any[], manager_email: string, context: 'project' | 'account' | 'default' = 'project'): { [permission: string]: { read: boolean; write: boolean } } {
    // For 'default' context, use 'default_admin' permissions
    const contextForPermissions = context === 'default' ? 'default_admin' : context;
    const allPermissionKeys = getAllPermissionKeys(contextForPermissions);
    const permissionDict: { [permission: string]: { read: boolean; write: boolean } } = {};
    
    // Step 1: Grant ALL read permissions for litmusworld users
    allPermissionKeys.forEach(permission => {
      permissionDict[permission] = { read: true, write: false };
    });

    // Step 2: Check rules for write permissions (if rules exist)
    if (rules.length > 0) {
      rules.forEach(rule => {
        if (rule.permission_list && Array.isArray(rule.permission_list) && this.userHasAccess(rule, manager_email)) {
          rule.permission_list.forEach((permission: string) => {
            if (permissionDict[permission] && rule.permission_type === 'WRITE') {
              permissionDict[permission].write = true;
            }
          });
        }
      });
    }

    return permissionDict;
  }

  /**
   * Process rules only - standard rule-based permission checking
   * @param rules - Array of rules
   * @param manager_email - User's email address
   * @returns Object with permissions based on rules
   */
  private processRulesOnly(rules: any[], manager_email: string): { [permission: string]: { read: boolean; write: boolean } } {
    const permissionDict: { [permission: string]: { read: boolean; write: boolean } } = {};

    // Get all unique permissions from all rules
    const allPermissions = new Set<string>();
    rules.forEach(rule => {
      if (rule.permission_list && Array.isArray(rule.permission_list)) {
        rule.permission_list.forEach((permission: string) => {
          allPermissions.add(permission);
        });
      }
    });

    // Initialize all permissions with false access
    allPermissions.forEach(permission => {
      permissionDict[permission] = { read: false, write: false };
    });

    // Check each rule and update permissions based on user access
    rules.forEach(rule => {
      if (rule.permission_list && Array.isArray(rule.permission_list) && this.userHasAccess(rule, manager_email)) {
        rule.permission_list.forEach((permission: string) => {
          if (permissionDict[permission]) {
            if (rule.permission_type === 'READ') {
              permissionDict[permission].read = true;
            } else if (rule.permission_type === 'WRITE') {
              permissionDict[permission].write = true;
              permissionDict[permission].read = true;
            }
          }
        });
      }
    });

    return permissionDict;
  }

  /**
   * Check if user email is in client_ops_info array
   * @param manager_email - User's email address
   * @param brand_details - Brand details object
   * @returns boolean - true if user is in client_ops_info
   */
  private checkClientOpsAccess(manager_email: string, brand_details?: any): boolean {
    if (!brand_details || 
        !brand_details.project_info || 
        !brand_details.project_info.client_ops_info || 
        !Array.isArray(brand_details.project_info.client_ops_info)) {
      return false;
    }

    return brand_details.project_info.client_ops_info.some((client: any) => 
      client.email_address === manager_email
    );
  }

  /**
   * Check if user email is in client_poc_info array (for accounts)
   * @param manager_email - User's email address
   * @param account_details - Account details object
   * @returns boolean - true if user is in client_poc_info
   */
  private checkClientPocAccess(manager_email: string, account_details?: any): boolean {
    if (!account_details || 
        !account_details.client_poc_info || 
        !Array.isArray(account_details.client_poc_info)) {
      return false;
    }

    return account_details.client_poc_info.some((client: any) => 
      client.email_address === manager_email
    );
  }

}
